/**
 * RoutingRulePreview Component
 *
 * Displays JSONLogic routing rule in human-readable format.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getRoutingFields } from '../constants/routing-fields';
import type { Field } from 'react-querybuilder';

interface RoutingRulePreviewProps {
  jsonLogic: Record<string, unknown> | null;
  showRaw?: boolean;
  fields?: Field[];
}

/**
 * Convert JSONLogic to human-readable text
 */
function jsonLogicToHumanReadable(
  jsonLogic: Record<string, unknown> | null,
  fields: Field[],
  t: TFunction<string>
): string {
  if (!jsonLogic) return t('routing.rulePreview.noConditionSet', 'No condition set');

  const operatorLabels: Record<string, string> = {
    '<': t('routing.operators.isLessThan', 'is less than'),
    '<=': t('routing.operators.isLessThanOrEqual', 'is at most'),
    '>': t('routing.operators.isGreaterThan', 'is greater than'),
    '>=': t('routing.operators.isGreaterThanOrEqual', 'is at least'),
    '==': t('routing.operators.isEqualTo', 'equals'),
    '!=': t('routing.operators.isNotEqualTo', 'does not equal'),
    contains: t('routing.operators.isContains', 'contains'),
    notContains: t('routing.operators.isNotContains', 'does not contain'),
    beginsWith: t('routing.operators.isBeginsWith', 'begins with'),
    endsWith: t('routing.operators.isEndsWith', 'ends with'),
  };

  function getFieldLabel(fieldName: string): string {
    const field = fields.find((f) => f.name === fieldName);
    return field?.label || fieldName;
  }

  function parseRule(rule: Record<string, unknown>, depth = 0): string {
    // Check for combinator
    if ('and' in rule) {
      const rules = rule.and as Record<string, unknown>[];
      const parts = rules.map((r) => parseRule(r, depth + 1));
      return depth > 0 ? `(${parts.join(' AND ')})` : parts.join(' AND ');
    }

    if ('or' in rule) {
      const rules = rule.or as Record<string, unknown>[];
      const parts = rules.map((r) => parseRule(r, depth + 1));
      return depth > 0 ? `(${parts.join(' OR ')})` : parts.join(' OR ');
    }

    // Find operator
    for (const [op, label] of Object.entries(operatorLabels)) {
      if (op in rule) {
        const operands = rule[op] as unknown[];
        if (Array.isArray(operands) && operands.length >= 2) {
          const firstOperand = operands[0] as Record<string, unknown>;
          const fieldName =
            typeof firstOperand === 'object' && 'var' in firstOperand
              ? (firstOperand.var as string)
              : '?';
          const value = operands[1];

          return `${getFieldLabel(fieldName)} ${label} ${formatValue(value)}`;
        }
      }
    }

    // Handle 'in' operator (contains)
    if ('in' in rule) {
      const operands = rule.in as unknown[];
      if (Array.isArray(operands) && operands.length >= 2) {
        const secondOperand = operands[1] as Record<string, unknown>;
        const fieldName =
          typeof secondOperand === 'object' && 'var' in secondOperand
            ? (secondOperand.var as string)
            : '?';
        const value = operands[0];
        return `${getFieldLabel(fieldName)} ${t('routing.operators.isContains', 'contains')} ${formatValue(value)}`;
      }
    }

    // Handle 'startsWith' operator
    if ('startsWith' in rule) {
      const operands = rule.startsWith as unknown[];
      if (Array.isArray(operands) && operands.length >= 2) {
        const firstOperand = operands[0] as Record<string, unknown>;
        const fieldName =
          typeof firstOperand === 'object' && 'var' in firstOperand
            ? (firstOperand.var as string)
            : '?';
        const value = operands[1];
        return `${getFieldLabel(fieldName)} ${t('routing.operators.isBeginsWith', 'begins with')} ${formatValue(value)}`;
      }
    }

    // Handle 'endsWith' operator
    if ('endsWith' in rule) {
      const operands = rule.endsWith as unknown[];
      if (Array.isArray(operands) && operands.length >= 2) {
        const firstOperand = operands[0] as Record<string, unknown>;
        const fieldName =
          typeof firstOperand === 'object' && 'var' in firstOperand
            ? (firstOperand.var as string)
            : '?';
        const value = operands[1];
        return `${getFieldLabel(fieldName)} ${t('routing.operators.isEndsWith', 'ends with')} ${formatValue(value)}`;
      }
    }

    return JSON.stringify(rule);
  }

  function formatValue(value: unknown): string {
    if (typeof value === 'boolean') {
      return value ? t('routing.values.yes', 'Yes') : t('routing.values.no', 'No');
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    if (typeof value === 'string') {
      return `"${value}"`;
    }
    return String(value);
  }

  return parseRule(jsonLogic);
}

export function RoutingRulePreview({
  jsonLogic,
  showRaw = false,
  fields,
}: RoutingRulePreviewProps) {
  // Same as RoutingRuleBuilder — the labels come from
  // `constants/routing-fields.ts`, whose group headers live in `common`.
  const { t } = useTranslation(['settings', 'common']);

  const resolvedFields = useMemo(() => fields ?? getRoutingFields(t), [fields, t]);

  const humanReadable = useMemo(
    () => jsonLogicToHumanReadable(jsonLogic, resolvedFields, t),
    [jsonLogic, resolvedFields, t]
  );

  if (!jsonLogic) {
    return (
      <div className="p-4 bg-bg-surface border border-border rounded-lg">
        <p className="text-sm text-fg-muted italic">
          {t(
            'routing.rulePreview.emptyState',
            'No conditions defined. Add rules above to create routing conditions.'
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Human-readable preview */}
      <div className="p-4 bg-bg-surface border border-border rounded-lg">
        <p className="text-xs font-semibold text-fg-muted uppercase mb-2">
          {t('routing.rulePreview.title', 'Condition Preview')}
        </p>
        <p className="text-sm text-fg" data-testid="routing-rule-preview-text">
          {t('routing.rulePreview.routeWhen', 'Route when:')}{' '}
          <span className="font-medium">{humanReadable}</span>
        </p>
      </div>

      {/* Raw JSON preview */}
      {showRaw && (
        <div className="p-4 bg-bg-surface border border-bg-elevated rounded-lg">
          <p className="text-xs font-semibold text-fg-muted uppercase mb-2">
            {t('routing.rulePreview.jsonLogicOutput', 'JSONLogic Output')}
          </p>
          <pre
            className="text-xs text-fg font-mono overflow-x-auto whitespace-pre-wrap"
            data-testid="routing-rule-preview-json"
          >
            {JSON.stringify(jsonLogic, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default RoutingRulePreview;
